import axios, { AxiosResponse, AxiosError } from 'axios';

// Define interfaces for JobSeeker and Company profiles
export interface UserProfile {
  id: number;
  email: string;
  role: 'JOB_SEEKER' | 'COMPANY' | 'ADMIN';
  verificationStatus: string;
  emailVerified: boolean;
}

export interface JobSeeker extends UserProfile {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  location?: string;
  profilePictureUrl?: string;
  bio?: string;
  dateOfBirth?: string; // Assuming ISO date string
  education?: string;
  educationLevel?: string;
  yearsOfExperience?: number;
  skills?: string[];
  currentPosition?: string;
  currentCompany?: string;
  expectedSalary?: number;
  availability?: string;
  resumeUrl?: string;
  linkedinUrl?: string;
  githubUrl?: string;
  portfolioUrl?: string;
  preferredWorkType?: string;
  createdAt?: string; // Assuming ISO date string
  updatedAt?: string; // Assuming ISO date string
  verified?: boolean;
  saIdNumber?: string;
  matricCertificateUrl?: string;
  profileCompleteness?: number;
}

export interface CompanyProfile extends UserProfile {
  companyName?: string;
  contactPerson?: string;
  phoneNumber?: string;
  location?: string;
  companyLogoUrl?: string;
  description?: string;
  industry?: string;
  companySize?: string;
  websiteUrl?: string;
  linkedinUrl?: string;
  foundedYear?: number;
  benefits?: string[];
  companyCulture?: string;
  createdAt?: string; // Assuming ISO date string
  updatedAt?: string; // Assuming ISO date string
  isVerified?: boolean;
  cipcCertificateUrl?: string;
  proofOfAddressUrl?: string;
  sarsTaxNumber?: string;
}

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://eazyhire-api-231030708559.africa-south1.run.app/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds timeout
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken: refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);

          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// API Methods
export const authAPI = {
  login: (email: string, password: string): Promise<AxiosResponse<{ accessToken: string; refreshToken: string; user: JobSeeker | CompanyProfile }>> =>
    api.post('/auth/login', { email, password }),
  
  register: (userData: any) =>
    api.post(`/auth/register/${userData.role.toLowerCase()}`, userData),
  
  refreshToken: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),
  
  logout: () =>
    api.post('/auth/logout'),
  
  getProfile: (): Promise<AxiosResponse<JobSeeker | CompanyProfile>> =>
    api.get('/auth/profile'),
  
  verifyEmail: (token: string) =>
    api.post('/auth/verify-email', { token }),
  
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  
  resetPassword: (token: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, newPassword }),
};

export const jobsAPI = {
  getJobs: (params?: any) =>
    api.get('/jobpostings', { params }),

  getMyJobs: (params?: any) =>
    api.get('/jobpostings/my-postings'),
  
  getJob: (id: number) =>
    api.get(`/jobpostings/${id}`),
  
  searchJobs: (params: any) =>
    api.get('/jobpostings/search', { params }),
  
  createJob: (jobData: any) =>
    api.post('/jobpostings', jobData),
  
  updateJob: (id: number, jobData: any) =>
    api.put(`/jobpostings/${id}`, jobData),
  
  deleteJob: (id: number) =>
    api.delete(`/jobpostings/${id}`),
};

export const jobApplicationsAPI = {
  getApplications: (params?: any) =>
    api.get('/applications', { params }),
  
  getApplication: (id: number) =>
    api.get(`/applications/${id}`),
  
  submitApplication: (applicationData: any) =>
    api.post('/applications', applicationData),
  
  oneClickApply: (jobId: number, source?: string) =>
    api.post(`/applications/one-click/${jobId}`, {}, { params: { source } }),
  
  // New/Updated methods
  getMyApplications: (page: number = 0, size: number = 10) =>
    api.get(`/applications/my-applications?page=${page}&size=${size}`),

  getApplicationsForJobPosting: (jobPostingId: number, page: number = 0, size: number = 10) =>
    api.get(`/applications/job/${jobPostingId}?page=${page}&size=${size}`),

  updateApplicationStatus: (id: number, data: { status: string; feedback?: string }) =>
    api.put(`/applications/${id}/status`, data),
  
  createApplicationFeedback: (id: number, feedbackData: any) =>
    api.post(`/applications/${id}/feedback`, feedbackData),

  getMyFeedback: () =>
    api.get('/applications/feedback/my-feedback'),

  getApplicationAnalytics: () =>
    api.get('/applications/analytics'),
};

export const usersAPI = {
  getProfile: (): Promise<AxiosResponse<{ user: JobSeeker | CompanyProfile, userType: 'jobseeker' | 'company', profileInsights: any }>> =>
    api.get('/users/profile'),
  
  updateProfile: (profileData: any) =>
    api.put('/users/profile', profileData),
  
  uploadDocument: (formData: FormData) =>
    api.post('/users/upload-document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  
  getVerificationStatus: () =>
    api.get('/users/verification-status'),
};

export const notificationsAPI = {
  getNotifications: (params?: any) =>
    api.get('/notifications', { params }),
  
  markAsRead: (id: number) =>
    api.put(`/notifications/${id}/read`),
  
  markAllAsRead: () =>
    api.put('/notifications/mark-all-read'),
  
  getUnreadCount: () =>
    api.get('/notifications/unread-count'),
  
  updatePreferences: (preferences: any) =>
    api.put('/notifications/preferences', preferences),
};

export const savedSearchesAPI = {
  getSavedSearches: () =>
    api.get('/saved-searches'),
  
  createSavedSearch: (searchData: any) =>
    api.post('/saved-searches', searchData),
  
  deleteSavedSearch: (id: number) =>
    api.delete(`/saved-searches/${id}`),
};

export const jobSeekersAPI = {
  getJobSeekers: (params?: any) =>
    api.get("/jobseekers", { params }),
  getJobSeeker: (id: number): Promise<AxiosResponse<JobSeeker>> =>
    api.get(`/jobseekers/${id}`),
}

export default api;
